import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuthStore } from '@/app/store/authStore';
import { useRole } from '@/app/hooks/useRole';

interface RoleProtectedRouteProps {
  /**
   * Array of allowed role names, or single role name
   * e.g., ['superuser', 'org_admin'] or 'client_manager'
   */
  allowedRoles: string | string[];

  /**
   * Path to redirect to if role check fails
   * Default: '/unauthorized'
   */
  redirectPath?: string;

  /**
   * Path to redirect to if user is not authenticated
   * Default: '/login'
   */
  loginPath?: string;
}

/**
 * RoleProtectedRoute Component
 *
 * A route wrapper that checks both authentication and role authorization.
 * Use this to protect entire route trees based on user roles.
 *
 * @example
 * // Protect routes for client_manager only
 * <Route element={<RoleProtectedRoute allowedRoles="client_manager" />}>
 *   <Route path="/client-manager/dashboard" element={<Dashboard />} />
 *   <Route path="/client-manager/tasks" element={<TaskList />} />
 * </Route>
 *
 * @example
 * // Protect routes for admins only
 * <Route element={<RoleProtectedRoute allowedRoles={['superuser', 'org_admin']} />}>
 *   <Route path="/admin/users" element={<UserManagement />} />
 * </Route>
 */
const RoleProtectedRoute: React.FC<RoleProtectedRouteProps> = ({
  allowedRoles,
  redirectPath = '/unauthorized',
  loginPath = '/login',
}) => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isLoading = useAuthStore((state) => state.isLoading);
  const user = useAuthStore((state) => state.user);
  const { roleName } = useRole();

  // First check: Is user authenticated?
  if (!isAuthenticated) {
    return <Navigate to={loginPath} replace />;
  }

  // Second check: Is user profile still loading?
  // If authenticated but no user data and still loading, wait for profile to load
  if (isAuthenticated && !user && isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Third check: Does user have a role assigned?
  if (!roleName) {
    // User is authenticated but no role after loading completed
    // This means they genuinely don't have a role assigned
    return <Navigate to={redirectPath} replace />;
  }

  // Fourth check: Is user's role in the allowed roles list?
  const rolesArray = Array.isArray(allowedRoles) ? allowedRoles : [allowedRoles];
  const hasAccess = rolesArray.includes(roleName);

  if (!hasAccess) {
    return <Navigate to={redirectPath} replace />;
  }

  // All checks passed, render child routes
  return <Outlet />;
};

export default RoleProtectedRoute;
